var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useCallback, useContext, useEffect, useId, useReducer, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, InputAdornment, List, ListItem, ListItemIcon, ListItemText, Paper, Stack, Step, StepLabel, Stepper, TextField, Typography, } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DrawIcon from "@mui/icons-material/Draw";
import ErrorIcon from "@mui/icons-material/Error";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { AuthContext } from "@/providers/AuthContextProvider";
import { TenantContext } from "@/providers/TenantContextProvider";
import { SigningApiError, SigningApiErrorKind, validatePanel, } from "@/lib/signing/api";
import { colonHex } from "@/lib/signing/der";
import { CertificateFileError, CertificateFileErrorCode, openFailureReason, } from "@/lib/signing/errors";
import { PayloadMismatchError, PayloadProblem, commonNameOf, documentKindOf, isOpenForSigning, pendingSigners, signerName, signerOf, } from "@/lib/signing/request";
import { CertificateCheckId, DocumentKind, SigningAction, SigningRequestStatus, } from "@/lib/signing/types";
import { SigningSubject, SigningSubjectVariant } from "./SigningSubject";
import { useSignedView } from "./useSignedView";
import { actionDescription, actionObject, formatList, organizationName, requestTitle, shortFingerprint, useSigningFormat, } from "./format";
// The crypto libraries (about 160 KB gzip) load when a dialog first opens.
var cryptoModule;
var loadCrypto = function () {
    cryptoModule !== null && cryptoModule !== void 0 ? cryptoModule : (cryptoModule = import("@/lib/signing/sign").catch(function (error) {
        cryptoModule = undefined;
        throw error;
    }));
    return cryptoModule;
};
export var SigningDialogStep;
(function (SigningDialogStep) {
    SigningDialogStep[SigningDialogStep["Check"] = 0] = "Check";
    SigningDialogStep[SigningDialogStep["Certificate"] = 1] = "Certificate";
    SigningDialogStep[SigningDialogStep["Signed"] = 2] = "Signed";
})(SigningDialogStep || (SigningDialogStep = {}));
var CertificatePhase;
(function (CertificatePhase) {
    CertificatePhase["Choose"] = "choose";
    CertificatePhase["Opening"] = "opening";
    CertificatePhase["Checking"] = "checking";
    CertificatePhase["Checked"] = "checked";
    CertificatePhase["Signing"] = "signing";
})(CertificatePhase || (CertificatePhase = {}));
var INITIAL = {
    step: SigningDialogStep.Check,
    confirmed: false,
    file: null,
    password: "",
    showPassword: false,
    phase: CertificatePhase.Choose,
    certificate: null,
    choices: [],
    ambiguous: false,
    checks: null,
    fileError: null,
    closed: null,
    error: null,
    signed: null,
};
var reducer = function (state, action) {
    var _a;
    switch (action.type) {
        case "reset":
            return INITIAL;
        case "confirm":
            return __assign(__assign({}, state), { confirmed: action.confirmed });
        case "goto":
            return __assign(__assign({}, state), { step: action.step, error: null });
        case "file":
            // A new file starts over: its password, key and checks are another's.
            return __assign(__assign({}, state), { file: action.file, password: "", phase: CertificatePhase.Choose, certificate: null, choices: [], ambiguous: false, checks: null, fileError: null, error: null });
        case "password":
            return __assign(__assign({}, state), { password: action.password, fileError: null });
        case "togglePassword":
            return __assign(__assign({}, state), { showPassword: !state.showPassword });
        case "opening":
            return __assign(__assign({}, state), { phase: CertificatePhase.Opening, fileError: null, error: null });
        case "openFailed":
            return __assign(__assign({}, state), { phase: CertificatePhase.Choose, fileError: action.code });
        case "openError":
            return __assign(__assign({}, state), { phase: CertificatePhase.Choose, error: "signing.widget.openError" });
        case "opened":
            // The keys are open; the password is no longer needed.
            return __assign(__assign({}, state), { certificate: action.choices[0], choices: action.choices, ambiguous: action.ambiguous, password: "", showPassword: false });
        case "choose":
            return __assign(__assign({}, state), { certificate: (_a = state.choices[action.index]) !== null && _a !== void 0 ? _a : state.certificate });
        case "closed":
            return __assign(__assign({}, state), { phase: CertificatePhase.Checked, closed: action.closed, error: null });
        case "checking":
            return __assign(__assign({}, state), { phase: CertificatePhase.Checking, checks: null, error: null });
        case "checked":
            return __assign(__assign({}, state), { phase: CertificatePhase.Checked, checks: action.checks });
        case "checkFailed":
            return __assign(__assign({}, state), { phase: CertificatePhase.Checked, error: "signing.widget.checkError" });
        case "signing":
            return __assign(__assign({}, state), { phase: CertificatePhase.Signing, error: null });
        case "signFailed": {
            var refused_1 = action.refused;
            var checks = refused_1 && state.checks
                ? state.checks.some(function (check) { return check.id === refused_1; })
                    ? state.checks.map(function (check) {
                        return check.id === refused_1 ? __assign(__assign({}, check), { ok: false }) : check;
                    })
                    : __spreadArray(__spreadArray([], state.checks, true), [{ id: refused_1, ok: false, detail: null }], false)
                : state.checks;
            return __assign(__assign({}, state), { phase: CertificatePhase.Checked, checks: checks, error: action.error });
        }
        case "signed":
            return __assign(__assign({}, state), { step: SigningDialogStep.Signed, phase: CertificatePhase.Checked, signed: { at: action.at, result: action.result } });
    }
};
var allPassed = function (checks) {
    return !!checks && checks.length > 0 && checks.every(function (check) { return check.ok; });
};
var failed = function (checks, id) {
    return !!(checks === null || checks === void 0 ? void 0 : checks.some(function (check) { return check.id === id && !check.ok; }));
};
/**
 * Why a freshly fetched request can no longer be signed as the dialog shows
 * it, or `null` while it still waits unchanged.
 */
var closedOf = function (fresh, shown) {
    var request = fresh.request;
    if (request.payload_sha256 !== shown.request.payload_sha256 ||
        request.code !== shown.request.code) {
        return { message: "signing.widget.closed.changed" };
    }
    switch (request.status) {
        case SigningRequestStatus.Cancelled:
            return {
                message: "signing.dialog.problems.cancelled",
                reason: request.cancel_reason
                    ? "signing.cancelReasons.".concat(request.cancel_reason)
                    : "signing.status.cancelled",
            };
        case SigningRequestStatus.Expired:
            return { message: "signing.widget.panel.expired" };
        case SigningRequestStatus.Completed:
        case SigningRequestStatus.Executed:
            return { message: "signing.widget.closed.allSigned" };
        case SigningRequestStatus.Failed:
            return { message: "signing.widget.panel.failed" };
        default:
            return isOpenForSigning(request) ? null : { message: "signing.widget.panel.expired" };
    }
};
var signErrorKey = function (error) {
    if (error instanceof SigningApiError) {
        if (error.kind === SigningApiErrorKind.Refused) {
            return { error: "signing.widget.refused", refused: error.check };
        }
        if (error.kind === SigningApiErrorKind.Stale) {
            return { error: "signing.widget.stale" };
        }
    }
    if (error instanceof PayloadMismatchError) {
        return {
            error: error.problem === PayloadProblem.Document
                ? "signing.widget.documentMismatch"
                : "signing.widget.mismatch",
        };
    }
    return { error: "signing.widget.signError" };
};
/** "Signing happens in this browser…": on every step (design §9). */
var LocalSigningNote = function () {
    var t = useTranslation().t;
    return (_jsx(Alert, { severity: "info", icon: _jsx(LockOutlinedIcon, { fontSize: "inherit" }), sx: { mt: 3 }, children: t("signing.dialog.localNote") }));
};
/**
 * The Post the signer signs for: the request's, where its signers are the
 * Post's own (an SBEI signs for their Post). A voter's approval is the voter's
 * Post, not the signer's, which the panel doesn't know: none.
 */
var signerPost = function (data) {
    return data.request.action === SigningAction.ApproveVoter ? null : data.election_name;
};
var CheckStep = function (_a) {
    var data = _a.data, api = _a.api, confirmed = _a.confirmed, onConfirm = _a.onConfirm;
    var t = useTranslation().t;
    var _b = useContext(AuthContext), userId = _b.userId, firstName = _b.firstName, username = _b.username;
    var me = signerOf(data, userId);
    var name = me ? signerName(me) : firstName || username;
    var post = signerPost(data);
    var role = (me === null || me === void 0 ? void 0 : me.title)
        ? post
            ? t("signing.dialog.check.titlePost", { title: me.title, post: post })
            : me.title
        : post;
    return (_jsxs(Stack, { spacing: 2, children: [_jsxs(Typography, { color: "text.secondary", children: [t("signing.dialog.check.signingAs", { name: name }), role ? " \u00B7 ".concat(role) : ""] }), _jsxs(Box, { children: [_jsx(Typography, { variant: "h6", component: "p", children: requestTitle(t, data) }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: actionDescription(t, data.request.action, data.seals_ballots) })] }), _jsx(SigningSubject, { data: data, api: api, variant: SigningSubjectVariant.Dialog }), documentKindOf(data.request) !== DocumentKind.NoDocument ? (_jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: confirmed, onChange: function (event) { return onConfirm(event.target.checked); } }), label: t("signing.dialog.check.confirmDocument", {
                    object: actionObject(t, data.request.action),
                }) })) : null] }));
};
var CertificateCard = function (_a) {
    var _b, _c;
    var certificate = _a.certificate, timeZone = _a.timeZone;
    var t = useTranslation().t;
    var format = useSigningFormat(timeZone);
    return (_jsxs(Box, { children: [_jsx(Typography, { sx: { fontWeight: 700 }, children: (_b = certificate.commonName) !== null && _b !== void 0 ? _b : certificate.subject }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: t("signing.widget.certificateCard", {
                    issuer: (_c = certificate.issuerCommonName) !== null && _c !== void 0 ? _c : certificate.issuer,
                    date: format.date(certificate.notAfter),
                    algorithm: t("signing.widget.algorithms.".concat(certificate.algorithm)),
                }) }), _jsx(Typography, { variant: "caption", component: "p", color: "text.secondary", sx: { fontFamily: "monospace" }, title: colonHex(certificate.fingerprintSha256), children: t("signing.widget.fingerprint", {
                    fingerprint: shortFingerprint(certificate.fingerprintSha256),
                }) })] }));
};
/** Identity and backstop checks: the drafts list the five main checks, these only when they fail. */
var SHOWN_ONLY_WHEN_FAILED = new Set([
    CertificateCheckId.RegisteredToOther,
    CertificateCheckId.AlreadySigned,
    CertificateCheckId.PostBinding,
    CertificateCheckId.Signature,
]);
var CheckList = function (_a) {
    var checks = _a.checks, certificate = _a.certificate, timeZone = _a.timeZone;
    var t = useTranslation().t;
    var format = useSigningFormat(timeZone);
    var label = function (_a) {
        var _b;
        var id = _a.id, ok = _a.ok, detail = _a.detail;
        if (!ok) {
            switch (id) {
                case CertificateCheckId.TrustedIssuer:
                    return t("signing.widget.untrustedIssuer", {
                        issuer: (_b = certificate.issuerCommonName) !== null && _b !== void 0 ? _b : certificate.issuer,
                    });
                case CertificateCheckId.RegisteredToOther:
                    return detail
                        ? t("signing.dialog.checks.failed.registered-to-other", { name: detail })
                        : t("signing.widget.registeredToSomeoneElse");
                default:
                    return t("signing.dialog.checks.failed.".concat(id));
            }
        }
        switch (id) {
            case CertificateCheckId.TrustedIssuer:
                return detail
                    ? t("signing.dialog.checks.passed.trusted-issuer", { root: detail })
                    : t("signing.widget.checkPassedNoDetail.trusted-issuer");
            case CertificateCheckId.NotRevoked:
                return detail
                    ? t("signing.dialog.checks.passed.not-revoked", {
                        time: format.time(detail) || detail,
                    })
                    : t("signing.widget.checkPassedNoDetail.not-revoked");
            case CertificateCheckId.Registered:
                return detail
                    ? t("signing.dialog.checks.passed.registered", {
                        date: format.date(detail) || detail,
                    })
                    : t("signing.dialog.checks.first-use");
            default:
                return t("signing.dialog.checks.passed.".concat(id));
        }
    };
    return (_jsx(List, { dense: true, disablePadding: true, "aria-label": t("signing.widget.checksTitle"), children: checks
            .filter(function (check) { return !check.ok || !SHOWN_ONLY_WHEN_FAILED.has(check.id); })
            .map(function (check) { return (_jsxs(ListItem, { disableGutters: true, "data-check": check.id, "data-ok": check.ok, children: [_jsx(ListItemIcon, { sx: { minWidth: 32 }, children: check.ok ? (_jsx(CheckCircleIcon, { color: "success", fontSize: "small", "aria-hidden": true })) : (_jsx(ErrorIcon, { color: "error", fontSize: "small", "aria-hidden": true })) }), _jsx(ListItemText, { primary: label(check), slotProps: { primary: { color: check.ok ? "text.primary" : "error" } } })] }, check.id)); }) }));
};
var CertificateStep = function (_a) {
    var _b;
    var state = _a.state, dispatch = _a.dispatch, onRetryChecks = _a.onRetryChecks, onChoose = _a.onChoose, timeZone = _a.timeZone;
    var t = useTranslation().t;
    var tenant = useContext(TenantContext).tenant;
    var passwordId = useId();
    var fileInput = useRef(null);
    var file = state.file, certificate = state.certificate, checks = state.checks, phase = state.phase, fileError = state.fileError;
    var busy = phase === CertificatePhase.Opening || phase === CertificatePhase.Signing;
    var organization = (_b = organizationName(tenant)) !== null && _b !== void 0 ? _b : t("signing.widget.organization");
    var problem = !checks
        ? null
        : failed(checks, CertificateCheckId.TrustedIssuer)
            ? t("signing.dialog.problems.issuerNotAccepted", { organization: organization })
            : failed(checks, CertificateCheckId.RegisteredToOther) ||
                failed(checks, CertificateCheckId.Registered)
                ? t("signing.dialog.problems.notForYou")
                : allPassed(checks)
                    ? null
                    : t("signing.widget.cantSign");
    var fileErrorText = fileError === CertificateFileErrorCode.WrongPassword
        ? t("signing.dialog.problems.wrongPassword")
        : fileError
            ? t("signing.widget.fileErrors.".concat(fileError))
            : null;
    return (_jsxs(Stack, { spacing: 2, children: [_jsx(Typography, { children: t("signing.dialog.certificate.intro") }), _jsxs(Paper, { variant: "outlined", sx: { p: 1.5, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }, children: [_jsx(InsertDriveFileOutlinedIcon, { color: "action", "aria-hidden": true }), _jsx(Box, { sx: { flex: 1, minWidth: 0 }, children: file ? (_jsxs(Typography, { component: "span", sx: { fontWeight: 600, overflowWrap: "anywhere" }, children: [file.name, _jsxs(Typography, { component: "span", color: "text.secondary", children: [" · ", t("signing.widget.fileSize", {
                                            size: (file.size / 1024).toFixed(1),
                                        })] })] })) : null }), _jsx(Button, { variant: "outlined", disabled: busy, onClick: function () { var _a; return (_a = fileInput.current) === null || _a === void 0 ? void 0 : _a.click(); }, children: file
                            ? t("signing.dialog.certificate.chooseAnother")
                            : t("signing.widget.chooseFile") }), _jsx("input", { ref: fileInput, hidden: true, type: "file", accept: ".p12,.pfx,application/x-pkcs12", "aria-label": t("signing.widget.fileInput"), onChange: function (event) {
                            var _a, _b;
                            dispatch({ type: "file", file: (_b = (_a = event.target.files) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : null });
                            event.target.value = "";
                        } })] }), certificate ? (_jsx(Paper, { variant: "outlined", sx: { p: 2 }, "data-testid": "signing-certificate", children: _jsxs(Stack, { spacing: 1.5, children: [state.ambiguous ? (_jsx(TextField, { select: true, size: "small", label: t("signing.widget.chooseCertificate"), value: state.choices.indexOf(certificate), disabled: busy, onChange: function (event) { return onChoose(Number(event.target.value)); }, slotProps: { select: { native: true } }, children: state.choices.map(function (choice, index) {
                                var _a;
                                return (_jsx("option", { value: index, children: [
                                        (_a = choice.commonName) !== null && _a !== void 0 ? _a : choice.subject,
                                        choice.friendlyName,
                                        shortFingerprint(choice.fingerprintSha256),
                                    ]
                                        .filter(Boolean)
                                        .join(" · ") }, choice.fingerprintSha256));
                            }) })) : null, _jsx(CertificateCard, { certificate: certificate, timeZone: timeZone }), phase === CertificatePhase.Checking ? (_jsxs(Stack, { direction: "row", spacing: 1, sx: { alignItems: "center" }, children: [_jsx(CircularProgress, { size: 16, "aria-hidden": true }), _jsx(Typography, { variant: "body2", children: t("signing.widget.checking") })] })) : checks ? (_jsx(CheckList, { checks: checks, certificate: certificate, timeZone: timeZone })) : null] }) })) : (_jsxs(_Fragment, { children: [_jsx(TextField, { id: passwordId, label: t("signing.dialog.certificate.password"), 
                        // A masked text field, not type=password: the laptop is shared and
                        // the browser must not offer to save the certificate's password.
                        type: "text", value: state.password, onChange: function (event) {
                            return dispatch({ type: "password", password: event.target.value });
                        }, autoComplete: "one-time-code", disabled: !file || busy, error: !!fileErrorText, helperText: fileErrorText, fullWidth: true, slotProps: {
                            htmlInput: {
                                "spellCheck": false,
                                "autoCapitalize": "off",
                                "data-lpignore": "true",
                                "data-1p-ignore": "true",
                                "data-testid": "certificate-password",
                                "style": {
                                    WebkitTextSecurity: state.showPassword ? "none" : "disc",
                                },
                            },
                            input: {
                                endAdornment: (_jsx(InputAdornment, { position: "end", children: _jsx(IconButton, { onClick: function () { return dispatch({ type: "togglePassword" }); }, "aria-label": state.showPassword
                                            ? t("signing.widget.hidePassword")
                                            : t("signing.widget.showPassword"), edge: "end", disabled: !file || busy, children: state.showPassword ? (_jsx(VisibilityOffIcon, {})) : (_jsx(VisibilityIcon, {})) }) })),
                            },
                        } }), phase === CertificatePhase.Opening ? (_jsxs(Stack, { direction: "row", spacing: 1, sx: { alignItems: "center" }, role: "status", children: [_jsx(CircularProgress, { size: 16, "aria-hidden": true }), _jsx(Typography, { variant: "body2", children: t("signing.widget.opening") })] })) : null] })), state.closed ? (_jsx(Alert, { severity: "warning", "data-testid": "signing-closed", children: t(state.closed.message, {
                    reason: state.closed.reason ? t(state.closed.reason) : undefined,
                }) })) : problem ? (_jsx(Alert, { severity: "error", children: problem })) : null, state.error ? (_jsx(Alert, { severity: "error", action: state.error === "signing.widget.checkError" ? (_jsx(Button, { color: "inherit", size: "small", onClick: onRetryChecks, children: t("signing.widget.retry") })) : undefined, children: t(state.error) })) : null] }));
};
var SignedStep = function (_a) {
    var _b;
    var data = _a.data, state = _a.state;
    var _c = useTranslation(), t = _c.t, i18n = _c.i18n;
    var format = useSigningFormat(data.time_zone);
    var userId = useContext(AuthContext).userId;
    var signed = state.signed;
    if (!signed)
        return null;
    var _d = signed.result, count = _d.count, required = _d.required;
    var next = count < required ? pendingSigners(data, userId).map(signerName) : [];
    var certificateName = state.certificate
        ? ((_b = state.certificate.commonName) !== null && _b !== void 0 ? _b : commonNameOf(state.certificate.subject))
        : "";
    return (_jsxs(Stack, { spacing: 1.5, sx: { alignItems: "center", textAlign: "center", py: 2 }, children: [_jsx(CheckCircleIcon, { color: "success", sx: { fontSize: 56 }, "aria-hidden": true }), _jsx(Typography, { variant: "h5", component: "p", children: t("signing.dialog.signed.title") }), _jsx(Typography, { children: requestTitle(t, data) }), _jsxs(Typography, { color: "text.secondary", children: [format.time(signed.at), " · ", t("signing.dialog.signed.withCertificate", { name: certificateName })] }), _jsx(Typography, { sx: { fontWeight: 700 }, children: count >= required
                    ? t("signing.dialog.signed.allIn", { total: required })
                    : t("signing.dialog.signed.count", { n: count, total: required }) }), next.length ? (_jsx(Typography, { color: "text.secondary", children: t("signing.dialog.signed.next", { names: formatList(next, i18n.language) }) })) : null] }));
};
/**
 * Signs a request in three steps: check what is signed, open the certificate
 * file in the browser (the server dry-runs its checks), sign. The file, the
 * key and the password never leave the browser; the dialog forgets them
 * when it closes.
 */
export var SigningDialog = function (_a) {
    var open = _a.open, data = _a.data, api = _a.api, onClose = _a.onClose, onSigned = _a.onSigned, onRequestChanged = _a.onRequestChanged;
    var t = useTranslation().t;
    var titleId = useId();
    var _b = useReducer(reducer, INITIAL), state = _b[0], dispatch = _b[1];
    // Results of work started for an earlier file or dialog are dropped.
    var generation = useRef(0);
    // One approval at a time, whatever the rendering does with fast clicks.
    var signing = useRef(false);
    var requestId = data.request.id;
    var signedView = useSignedView(data);
    useEffect(function () {
        generation.current += 1;
        dispatch({ type: "reset" });
        if (open) {
            loadCrypto().catch(function () { return undefined; });
        }
    }, [open, requestId]);
    useEffect(function () {
        generation.current += 1;
    }, [state.file]);
    var runChecks = useCallback(function (certificate, current) { return __awaiter(void 0, void 0, void 0, function () {
        var checks, _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    dispatch({ type: "checking" });
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, api.checkCertificate(requestId, {
                            chain_pem: certificate.chainPem,
                        })];
                case 2:
                    checks = (_b.sent()).checks;
                    if (current === generation.current)
                        dispatch({ type: "checked", checks: checks });
                    return [3 /*break*/, 4];
                case 3:
                    _a = _b.sent();
                    if (current === generation.current)
                        dispatch({ type: "checkFailed" });
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); }, [api, requestId]);
    var openCertificate = function () { return __awaiter(void 0, void 0, void 0, function () {
        var file, password, current, bytes, crypto_1, _a, _b, choices, ambiguous, error_1;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    file = state.file, password = state.password;
                    if (!file)
                        return [2 /*return*/];
                    current = generation.current;
                    dispatch({ type: "opening" });
                    bytes = null;
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 7, 8, 9]);
                    return [4 /*yield*/, loadCrypto()];
                case 2:
                    crypto_1 = _c.sent();
                    _a = Uint8Array.bind;
                    return [4 /*yield*/, file.arrayBuffer()];
                case 3:
                    bytes = new (_a.apply(Uint8Array, [void 0, _c.sent()]))();
                    // Let "Opening…" paint: key derivation is synchronous and can take seconds.
                    return [4 /*yield*/, new Promise(function (resolve) { return setTimeout(resolve, 0); })];
                case 4:
                    // Let "Opening…" paint: key derivation is synchronous and can take seconds.
                    _c.sent();
                    return [4 /*yield*/, crypto_1.openP12Choices(bytes, password)];
                case 5:
                    _b = _c.sent(), choices = _b.choices, ambiguous = _b.ambiguous;
                    if (current !== generation.current)
                        return [2 /*return*/];
                    dispatch({ type: "opened", choices: choices, ambiguous: ambiguous });
                    return [4 /*yield*/, runChecks(choices[0], current)];
                case 6:
                    _c.sent();
                    return [3 /*break*/, 9];
                case 7:
                    error_1 = _c.sent();
                    if (current !== generation.current)
                        return [2 /*return*/];
                    if (!(error_1 instanceof CertificateFileError)) {
                        // Not the file's fault (e.g. the crypto module didn't load): nothing to log.
                        dispatch({ type: "openError" });
                        return [2 /*return*/];
                    }
                    dispatch({ type: "openFailed", code: error_1.code });
                    // Only the file name and the reason: never the file or the password.
                    api.reportOpenFailure(requestId, {
                        file_name: file.name,
                        reason: openFailureReason(error_1.code),
                    }).catch(function () { return undefined; });
                    return [3 /*break*/, 9];
                case 8:
                    bytes === null || bytes === void 0 ? void 0 : bytes.fill(0);
                    return [7 /*endfinally*/];
                case 9: return [2 /*return*/];
            }
        });
    }); };
    /** The request as it is now; a closed or changed one ends the attempt. */
    var refetch = function () { return __awaiter(void 0, void 0, void 0, function () {
        var fresh, _a, closed;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _a = validatePanel;
                    return [4 /*yield*/, api.getRequest(requestId)];
                case 1:
                    fresh = _a.apply(void 0, [_b.sent()]);
                    closed = closedOf(fresh, data);
                    if (closed)
                        onRequestChanged === null || onRequestChanged === void 0 ? void 0 : onRequestChanged();
                    return [2 /*return*/, closed !== null && closed !== void 0 ? closed : fresh];
            }
        });
    }); };
    var sign = function () { return __awaiter(void 0, void 0, void 0, function () {
        var certificate, current, crypto_2, fresh, result, error_2, now;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    certificate = state.certificate;
                    if (!certificate || !allPassed(state.checks) || state.closed || signing.current)
                        return [2 /*return*/];
                    signing.current = true;
                    current = generation.current;
                    dispatch({ type: "signing" });
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 5, 8, 9]);
                    return [4 /*yield*/, loadCrypto()
                        // Right before signing: still waiting, still the payload shown, a fresh document URL.
                    ];
                case 2:
                    crypto_2 = _a.sent();
                    return [4 /*yield*/, refetch()];
                case 3:
                    fresh = _a.sent();
                    if (current !== generation.current)
                        return [2 /*return*/];
                    if (!("request" in fresh)) {
                        dispatch({ type: "closed", closed: fresh });
                        return [2 /*return*/];
                    }
                    return [4 /*yield*/, crypto_2.signAndApprove(api, fresh, certificate)];
                case 4:
                    result = _a.sent();
                    if (current !== generation.current)
                        return [2 /*return*/];
                    dispatch({ type: "signed", result: result, at: new Date() });
                    onSigned === null || onSigned === void 0 ? void 0 : onSigned(result);
                    return [3 /*break*/, 9];
                case 5:
                    error_2 = _a.sent();
                    if (current !== generation.current)
                        return [2 /*return*/];
                    if (error_2 instanceof SigningApiError &&
                        error_2.kind === SigningApiErrorKind.AlreadySigned) {
                        dispatch({ type: "closed", closed: { message: "signing.widget.alreadySigned" } });
                        onRequestChanged === null || onRequestChanged === void 0 ? void 0 : onRequestChanged();
                        return [2 /*return*/];
                    }
                    if (!!(error_2 instanceof SigningApiError && error_2.kind === SigningApiErrorKind.Refused)) return [3 /*break*/, 7];
                    return [4 /*yield*/, refetch().catch(function () { return null; })];
                case 6:
                    now = _a.sent();
                    if (current !== generation.current)
                        return [2 /*return*/];
                    if (now && !("request" in now)) {
                        dispatch({ type: "closed", closed: now });
                        return [2 /*return*/];
                    }
                    _a.label = 7;
                case 7:
                    dispatch(__assign({ type: "signFailed" }, signErrorKey(error_2)));
                    return [3 /*break*/, 9];
                case 8:
                    signing.current = false;
                    return [7 /*endfinally*/];
                case 9: return [2 /*return*/];
            }
        });
    }); };
    var busy = state.phase === CertificatePhase.Opening || state.phase === CertificatePhase.Signing;
    var close = function () {
        if (!busy)
            onClose();
    };
    var needsConfirmation = documentKindOf(data.request) !== DocumentKind.NoDocument;
    var steps = [
        t("signing.dialog.steps.check"),
        t("signing.dialog.steps.certificate"),
        t("signing.dialog.steps.signed"),
    ];
    return (_jsxs(Dialog, { open: open, onClose: close, fullWidth: true, maxWidth: "sm", "aria-labelledby": titleId, children: [_jsxs(DialogTitle, { id: titleId, sx: { display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(DrawIcon, { "aria-hidden": true }), t("signing.dialog.title", { object: actionObject(t, data.request.action) })] }), _jsxs(DialogContent, { children: [_jsx(Stepper, { activeStep: state.signed ? steps.length : state.step, sx: { mb: 3, mt: 1 }, children: steps.map(function (label) { return (_jsx(Step, { children: _jsx(StepLabel, { children: label }) }, label)); }) }), state.step === SigningDialogStep.Check ? (_jsx(CheckStep, { data: data, api: api, confirmed: state.confirmed, onConfirm: function (confirmed) { return dispatch({ type: "confirm", confirmed: confirmed }); } })) : state.step === SigningDialogStep.Certificate ? (_jsx(CertificateStep, { state: state, onChoose: function (index) {
                            var choice = state.choices[index];
                            if (!choice)
                                return;
                            dispatch({ type: "choose", index: index });
                            runChecks(choice, generation.current).catch(function () { return undefined; });
                        }, timeZone: data.time_zone, dispatch: dispatch, onRetryChecks: function () {
                            if (state.certificate) {
                                runChecks(state.certificate, generation.current).catch(function () { return undefined; });
                            }
                        } })) : (_jsx(SignedStep, { data: data, state: state })), _jsx(LocalSigningNote, {})] }), _jsx(DialogActions, { sx: { px: 3, pb: 2 }, children: state.step === SigningDialogStep.Check ? (_jsxs(_Fragment, { children: [_jsx(Button, { onClick: close, children: t("signing.dialog.cancel") }), _jsx(Button, { variant: "contained", disabled: !signedView.verified || (needsConfirmation && !state.confirmed), onClick: function () {
                                return dispatch({ type: "goto", step: SigningDialogStep.Certificate });
                            }, children: t("signing.widget.continue") })] })) : state.step === SigningDialogStep.Certificate ? (_jsxs(_Fragment, { children: [_jsx(Button, { onClick: close, disabled: busy, children: t("signing.dialog.cancel") }), state.certificate ? (_jsx(Button, { variant: "contained", startIcon: state.phase === CertificatePhase.Signing ? (_jsx(CircularProgress, { size: 16, color: "inherit", "aria-hidden": true })) : (_jsx(DrawIcon, {})), disabled: state.phase !== CertificatePhase.Checked ||
                                !allPassed(state.checks) ||
                                !!state.closed, onClick: function () {
                                sign().catch(function () { return undefined; });
                            }, children: state.phase === CertificatePhase.Signing
                                ? t("signing.widget.signing")
                                : t("signing.dialog.sign") })) : (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "outlined", disabled: busy, onClick: function () {
                                        return dispatch({ type: "goto", step: SigningDialogStep.Check });
                                    }, children: t("signing.dialog.back") }), _jsx(Button, { variant: "contained", disabled: !state.file || busy, onClick: function () {
                                        openCertificate().catch(function () { return undefined; });
                                    }, children: t("signing.dialog.certificate.open") })] }))] })) : (_jsx(Button, { variant: "contained", onClick: onClose, children: t("signing.widget.done") })) })] }));
};
